import { token as authToken, handleAuthExpired, AUTH_EXPIRED_MESSAGE } from './authState.js'

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

// 从非 OK 响应中取出可读的错误说明
// 服务端常见两种：JSON（{ message }，如 /login）与纯文本（如鉴权中间件直接返回字符串）
async function readErrorMessage(response, resourceName) {
  const text = await response.text().catch(() => '')
  if (text) {
    try {
      const data = JSON.parse(text)
      if (data && typeof data.message === 'string' && data.message) return data.message
    } catch {
      // 非 JSON，按纯文本处理
    }
    return text
  }
  return resourceName ? `${resourceName}失败: ${response.status}` : `请求失败: ${response.status}`
}

async function request(url, options = {}) {
  // skipAuthExpiry 是本模块自己的开关，必须摘掉，不能透传给 fetch（未知选项）
  const { skipAuthExpiry = false, ...fetchOptions } = options
  const token = authToken.value
  const headers = {
    ...(fetchOptions.headers || {})
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...fetchOptions,
    headers
  })

  if (response.status === 401) {
    // 登录接口的 401 可能是「账号或密码错误」，不能当成登录过期处理
    if (skipAuthExpiry) {
      // 显示服务端返回的真实原因（如「账号或密码错误」）
      throw new Error(await readErrorMessage(response, '登录'))
    }
    // 清空共享登录态：组件里的 isLoggedIn 随之变为 false，登录卡片立即渲染（无需刷新）
    // handleAuthExpired 内部已弹出「登录已过期，请重新登录」，这里不再重复提示
    handleAuthExpired()
    const err = new Error(AUTH_EXPIRED_MESSAGE)
    err.isAuthExpired = true
    throw err
  }

  if (!response.ok) {
    throw new Error(await readErrorMessage(response))
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }
  return response
}

export function submitHazard(formData) {
  return request('/submit', {
    method: 'POST',
    body: formData
  })
}

export function fetchOrg() {
  return request('/org')
}

export function updateOrg(departments) {
  return request('/org', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ departments })
  })
}

export function loginAdmin(credentials) {
  return request('/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(credentials),
    // 登录失败的 401 属于「账号或密码错误」，不要清登录态、不要提示登录过期，
    // 直接把服务端返回的真实原因（message）抛给登录表单显示
    skipAuthExpiry: true
  })
}

export function fetchHazards(params = {}) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value)
    }
  })
  return request(`/list?${query.toString()}`)
}

export function updateHazard(data) {
  return request('/update', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
}

export function exportHazards(params = {}) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value)
    }
  })
  return request(`/export?${query.toString()}`)
}

export function getImageUrl(id, index = 0) {
  const token = authToken.value
  return `${API_BASE}/image?id=${id}&index=${index}&token=${encodeURIComponent(token || '')}`
}

export function getVideoUrl(id, index = 0) {
  const token = authToken.value
  return `${API_BASE}/video?id=${id}&index=${index}&token=${encodeURIComponent(token || '')}`
}

export function getDownloadUrl(id, index = 0) {
  const token = authToken.value
  return `${API_BASE}/download?id=${id}&index=${index}&token=${encodeURIComponent(token || '')}`
}

export function markSynced(ids) {
  return request('/sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ ids })
  })
}
